import { useRef, useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import clsx from 'clsx';
import { TrendingUp, TrendingDown, Zap, Target, Activity, CloudRain, Wind } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import type { IntelligenceSignal } from '../../types';

// ── Signal type icon ──────────────────────────────────────────────────────────

function SignalIcon({ type }: { type: string }) {
  const cls = 'w-2.5 h-2.5 flex-shrink-0';
  if (type === 'steam_move')    return <Zap className={cls} />;
  if (type === 'sharp_action' || type === 'sharp_alignment') return <Target className={cls} />;
  if (type === 'weather_alert') return <Wind className={cls} />;
  if (type === 'injury_downgrade') return <CloudRain className={cls} />;
  return <Activity className={cls} />;
}

// ── Single radar item ─────────────────────────────────────────────────────────

function RadarItem({ signal }: { signal: IntelligenceSignal }) {
  const hasMovement = !!signal.movement;
  const isPositive = signal.movement
    ? signal.movement.startsWith('+') || signal.movement.startsWith('↑')
    : false;

  const toneCls = signal.severity === 'high'
    ? 'text-gold'
    : signal.severity === 'warn'
      ? 'text-warn'
      : 'text-info';

  const typeBadgeCls = signal.severity === 'high'
    ? 'bg-gold/10 text-gold border-gold/20'
    : signal.severity === 'warn'
      ? 'bg-warn/10 text-warn border-warn/20'
      : 'bg-info/10 text-info border-info/20';

  return (
    <div className={clsx(
      'flex items-center gap-2 px-4 py-0 flex-shrink-0 border-r border-surface-4',
      toneCls,
    )}>
      {hasMovement && (
        isPositive
          ? <TrendingUp className="w-2.5 h-2.5 flex-shrink-0 text-win" />
          : <TrendingDown className="w-2.5 h-2.5 flex-shrink-0 text-loss" />
      )}
      <SignalIcon type={signal.type} />
      <span className="text-[10px] font-medium text-gray-300 whitespace-nowrap">{signal.title}</span>
      {signal.movement && (
        <span className={clsx('text-[10px] font-mono font-bold whitespace-nowrap', isPositive ? 'text-win' : 'text-loss')}>
          {signal.movement}
        </span>
      )}
      <span className={clsx('text-[8px] font-bold uppercase tracking-wide px-1 py-px rounded border whitespace-nowrap', typeBadgeCls)}>
        {signal.type.replace(/_/g, ' ')}
      </span>
    </div>
  );
}

// ── Ticker ────────────────────────────────────────────────────────────────────

interface MarketRadarTickerProps {
  className?: string;
}

export function MarketRadarTicker({ className }: MarketRadarTickerProps) {
  const signals = useAppStore((s) => s.signals);
  const stripRef = useRef<HTMLDivElement>(null);
  const controls = useAnimationControls();
  const [stripWidth, setStripWidth] = useState(0);

  const activeSignals = signals.filter((s) => s.isActive);

  useEffect(() => {
    if (!stripRef.current || activeSignals.length === 0) return;
    const w = stripRef.current.scrollWidth;
    setStripWidth(w);
    void controls.start({
      x: [0, -w],
      transition: {
        duration: Math.max(20, w / 60),
        ease: 'linear',
        repeat: Infinity,
        repeatType: 'loop',
      },
    });
  }, [activeSignals.length, controls]);

  if (activeSignals.length === 0) return null;

  return (
    <div
      className={clsx(
        'hidden md:flex items-center h-9 bg-surface-1 border-t border-surface-4 overflow-hidden flex-shrink-0',
        className,
      )}
    >
      {/* Label */}
      <div className="flex-shrink-0 flex items-center gap-1.5 px-3 h-full border-r border-surface-4 bg-surface-0">
        <span className="w-1.5 h-1.5 rounded-full bg-gold-bright animate-pulse-slow flex-shrink-0" />
        <span className="text-[9px] font-black uppercase tracking-widest text-gray-500 whitespace-nowrap">
          Market Radar
        </span>
      </div>

      {/* Scrolling strip */}
      <div className="flex-1 overflow-hidden relative">
        <motion.div
          ref={stripRef}
          animate={controls}
          className="flex items-center h-9"
          style={{ willChange: 'transform' }}
        >
          {/* Duplicate for seamless loop */}
          {[...activeSignals, ...activeSignals].map((signal, i) => (
            <RadarItem key={`${signal.id}-${i}`} signal={signal} />
          ))}
        </motion.div>
      </div>
    </div>
  );
}
